import { createContext, useContext, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Sheet, SheetBody, SheetContent, SheetHeader, SheetTitle, cn, type AppShellNavItem } from "@fox1ck-org/ui";

import { useIsPhone } from "@/lib/hooks/use-phone";
import { useT } from "@/lib/i18n";
import { RAIL_LABEL, RAIL_PATH, type RailId } from "@/shell/places";

/**
 * The shell's phone parts (доски MToday, MCampaigns, MCampaign, MReport):
 * the package's AppShell draws the 56px header and the bottom bar at 768px
 * and below; this file decides what is in them. The screens are the desktop
 * screens, laid out narrower (decision 10: a phone is adaptive, not a second
 * set of pages) — nothing here routes, counts or fetches.
 */

/** The bottom bar's four places, then «Ещё» (доски M*). */
const PHONE_TABS: readonly RailId[] = ["today", "campaigns", "reports", "journal"];
/** What «Ещё» holds: every other rail item, in the rail's order. */
const MORE: readonly RailId[] = ["health", "offers", "landers", "partners", "sources", "domains", "settings"];
const MORE_ID = "more";

/** Which bottom-bar item is lit: a place of its own, or «Ещё» for the rest. Pure. */
export function phoneActive(rail: RailId | undefined): string | undefined {
  if (!rail) return undefined;
  return PHONE_TABS.includes(rail) ? rail : MORE_ID;
}

/** The bottom bar: the four places with the period carried, and «Ещё». */
export function phoneNav(t: ReturnType<typeof useT>, carried: string, onMore: () => void): AppShellNavItem[] {
  return [
    ...PHONE_TABS.map((id) => ({ id, label: t(id === "reports" ? "adapt.tab.report" : RAIL_LABEL[id]), href: `${RAIL_PATH[id]}${carried}` })),
    { id: MORE_ID, label: t("adapt.tab.more"), onSelect: onMore },
  ];
}

/**
 * The header's title: the place's name, or — on an object's screen (a
 * campaign) — its name with the way back to the list (доска MCampaign).
 */
export function PhoneTitle({ title, back, backLabel }: { title: ReactNode; back?: string; backLabel?: string }) {
  if (!back) return <>{title}</>;
  return (
    <span className="flex min-w-0 items-center gap-1">
      <Link to={back} aria-label={backLabel} className="-ml-2 grid size-11 shrink-0 place-items-center rounded-md text-foreground hover:bg-surface-2">
        <ChevronLeft className="size-5" strokeWidth={1.5} />
      </Link>
      <span className="min-w-0 truncate">{title}</span>
    </span>
  );
}

/** «Ещё»: the rest of the rail and «Мои · Все», in a bottom sheet. Signing out is the account menu's (the header's avatar). */
export function PhoneMore({ open, onOpenChange, carried, scope }: { open: boolean; onOpenChange: (open: boolean) => void; carried: string; scope?: ReactNode }) {
  const t = useT();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" dismissOnSwipe closeLabel={t("panel.close")} className="max-h-[85dvh]">
        <SheetHeader>
          <SheetTitle>{t("adapt.tab.more")}</SheetTitle>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-3 px-4 pb-[max(env(safe-area-inset-bottom),--spacing(3))]">
          {scope ? <div className="flex items-center justify-between gap-3 text-sm">{t("scope.label")}{scope}</div> : null}
          <nav aria-label={t("adapt.tab.more")} className="flex flex-col overflow-hidden rounded-lg border border-border bg-card">
            {MORE.map((id) => (
              <Link
                key={id}
                to={`${RAIL_PATH[id]}${carried}`}
                onClick={() => onOpenChange(false)}
                className="flex min-h-12 items-center gap-3 border-t border-border px-3.5 text-sm first:border-t-0 hover:bg-surface-2"
              >
                <span className="min-w-0 flex-1 truncate">{t(RAIL_LABEL[id])}</span>
                <ChevronRight className="size-4 text-muted-foreground" strokeWidth={1.5} />
              </Link>
            ))}
          </nav>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

const SlotContext = createContext<HTMLElement | null>(null);

/**
 * The header's slot for a screen's own buttons (the list's filter, the
 * report's grouping): the shell owns the header, the screen owns what it
 * filters — a portal joins the two without the shell knowing any screen.
 */
export function usePhoneSlot(): [ReactNode, (node: ReactNode) => ReactNode] {
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  const target = <span ref={setSlot} className="contents" />;
  const provide = (node: ReactNode) => <SlotContext.Provider value={slot}>{node}</SlotContext.Provider>;
  return [target, provide];
}

/** A screen's buttons in the phone header, after the period. Nothing on a wider screen. */
export function PhoneActions({ children, className }: { children: ReactNode; className?: string }) {
  const slot = useContext(SlotContext);
  const phone = useIsPhone();
  if (!phone || !slot) return null;
  return createPortal(<span className={cn("contents", className)}>{children}</span>, slot);
}
